/**
 * <s-quick-view> — product quick look popup for collection cards.
 * Cards call it with: <button commandfor="quick-view" command="--open" data-template="quick-view-123"
 * data-url="/products/handle?view=quick">. The popup body (snippets/quick-view-content.liquid via
 * templates/product.quick.liquid) is fetched on first open and cached in a <template> with that id,
 * so collection pages stay light. An existing <template> with the id is used directly.
 */
class SQuickView extends HTMLElement {
  #ac;
  #loading = new Map();

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', (event) => {
      if (event.command !== '--open') return;
      const data = event.source?.dataset;
      if (data?.template) this.#open(data.template, data.url, event.source);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #ensureTemplate(id, url) {
    const existing = document.getElementById(id);
    if (existing instanceof HTMLTemplateElement) return existing;
    if (!url) return null;
    if (!this.#loading.has(id)) {
      this.#loading.set(id, (async () => {
        const response = await fetch(url, { signal: this.#ac?.signal });
        if (!response.ok) throw new Error(`Quick view ${response.status}`);
        const template = document.createElement('template');
        template.id = id;
        template.innerHTML = await response.text();
        this.append(template);
        return template;
      })().finally(() => this.#loading.delete(id)));
    }
    return this.#loading.get(id);
  }

  async #open(id, url, source) {
    const dialog = this.querySelector('dialog');
    const body = this.querySelector('[data-quick-view-body]');
    if (!dialog || !body) return;

    let template = null;
    source?.setAttribute('aria-busy', 'true');
    try {
      template = await this.#ensureTemplate(id, url);
    } catch {
      template = null;
    } finally {
      source?.removeAttribute('aria-busy');
    }
    if (!(template instanceof HTMLTemplateElement)) {
      // Fall back to the product page if the popup couldn't load.
      if (url) window.location.href = url.split('?')[0];
      return;
    }

    body.replaceChildren(template.content.cloneNode(true));
    const heading = body.querySelector('h2');
    if (heading) {
      heading.id = 'quick-view-title';
      dialog.removeAttribute('aria-label');
      dialog.setAttribute('aria-labelledby', 'quick-view-title');
    }
    if (!dialog.open) dialog.showModal();
    body.scrollTop = 0;
    dialog.scrollTop = 0;
  }
}

if (!customElements.get('s-quick-view')) customElements.define('s-quick-view', SQuickView);
